import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { BehaviorSubject, filter, switchMap, take, throwError, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { RefreshTokenResponse } from '../models/auth.models';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth endpoints and public signing page
  if (req.url.includes('/auth/login') ||
      req.url.includes('/auth/refresh') ||
      req.url.includes('/onboarding/sign/')) {
    return next(req);
  }

  return next(addToken(req, authService)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handle401Error(req, next, authService, router);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response: RefreshTokenResponse) => {
        isRefreshing = false;
        refreshTokenSubject.next(response.Token);
        return next(addToken(request, authService));
      }),
      catchError(err => {
        isRefreshing = false;
        authService.logout();
        router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  }

  // Queue concurrent 401 requests — wait for refresh to complete
  return refreshTokenSubject.pipe(
    filter(token => token !== null),
    take(1),
    switchMap(() => next(addToken(request, authService)))
  );
}

function addToken(request: HttpRequest<unknown>, authService: AuthService): HttpRequest<unknown> {
  const token = authService.getToken();
  if (!token) return request;
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` }
  });
}
